import type { ReactNode } from 'react';
import { PROJECT_LABEL_SLOTS, projectLabelSlot } from '../../shared/projects.js';
import { PATHS } from '../paths.js';
import { Chip } from './Chip.js';
import type { IconName } from './Icon.js';

/** What a chip needs of a project: its id (the colour's key and the link), its name (the text) and, when the server sent one, its slot. */
export interface ProjectChipProject {
  id: string;
  name: string;
  /** The label slot as the server computed it (`ProjectRefDto.slot`); recomputed from the id when absent. */
  slot?: number | null;
}

export interface ProjectChipProps {
  project: ProjectChipProject;
  /**
   * Link to the project's record page (the default). `false` renders a plain label — inside a form
   * control's `<label>`, a heading or a table caption, where a nested link would be wrong.
   */
  link?: boolean;
  /** A filter chip's dismiss control (03-components §9: at least 44px on touch, keyboard-operable). */
  onRemove?: () => void;
  /** Accessible name for the dismiss control, e.g. "Remove filter: project". */
  removeLabel?: string;
  /** Text before the name, e.g. "Project: " on a filter chip; the name stays the chip's text. */
  prefix?: ReactNode;
  icon?: IconName;
  className?: string;
  /** Defaults to the project's name, so a truncated chip still tells its whole name on hover. */
  title?: string;
}

/**
 * The slot a chip draws in: the server's when it sent a valid one, else the same hash of the id
 * (`projectLabelSlot`, shared with the server) — so a rename keeps its colour and the same project is
 * the same colour on every screen, whichever side computed it.
 */
export function projectChipSlot(project: ProjectChipProject): number {
  const { slot } = project;
  if (typeof slot === 'number' && Number.isInteger(slot) && slot >= 1 && slot <= PROJECT_LABEL_SLOTS) return slot;
  return projectLabelSlot(project.id);
}

/**
 * A project as every surface names it (design §7.6): a label chip — one construction, a solid
 * `--label-N` fill with `--label-N-fg` text — whose text is the project's name, so the word travels
 * with the colour and colour is never the only signal (01-foundations §1.8, §7.3).
 */
export function ProjectChip({ project, link = true, onRemove, removeLabel, prefix, icon, className, title }: ProjectChipProps) {
  const classes = ['project-chip', className].filter(Boolean).join(' ');
  return (
    <Chip
      tone="label"
      slot={projectChipSlot(project)}
      to={link ? PATHS.project(project.id) : undefined}
      onRemove={onRemove}
      removeLabel={removeLabel}
      icon={icon}
      className={classes}
      title={title ?? project.name}
    >
      {prefix}
      {project.name}
    </Chip>
  );
}
